import { FC, memo, useContext } from 'react';

import { useQuery } from '@apollo/client';
import {
  CircleCloseIcon,
  CirclesIcon,
  ConeIcon,
  ExclamationIcon,
  InfoIcon,
  Label,
  Separator,
  TechModeIcon,
  Tooltip,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { LocalizationContext } from 'localization';
import { EShellMode } from 'types';

import { DEFAULT, ICON_SIZE } from './constants';
import { IStatusesCount } from './interfaces';
import { HOSTS_STATUSES, IHostStatusesResponse } from './schema';
import s from './Statuses.module.scss';

export const Statuses: FC = memo(() => {
  const { t } = useContext(LocalizationContext);
  const { data } = useQuery<IHostStatusesResponse>(HOSTS_STATUSES, {
    fetchPolicy: 'cache-only',
  });

  const {
    off,
    shellOff,
    on,
    service,
    booking,
    lostConnection,
    session,
    highAccess,
    locked,
    configuration,
  } =
    data?.hostsOverview.reduce<IStatusesCount>(
      (
        acc,
        {
          client_sessions,
          bookings,
          online,
          in_service,
          shell_mode,
          device_has_changed,
        }
      ) => {
        if (online) {
          acc.on += 1;
        } else {
          acc.off += 1;
        }

        if (in_service) {
          acc.service += 1;
        }

        if (shell_mode === EShellMode.HIGH_ACCESS) {
          acc.highAccess += 1;
        }
        if (shell_mode === EShellMode.DISABLED) {
          acc.shellOff += 1;
        }

        if (client_sessions.length) {
          acc.session += 1;

          if (!online) {
            acc.lostConnection += 1;
          }
        }

        if (bookings.length) {
          acc.booking += 1;
        }

        if (device_has_changed) {
          acc.configuration += 1;
        }

        return acc;
      },
      {
        ...DEFAULT,
      }
    ) ?? DEFAULT;

  return (
    <div className={s.container}>
      <Tooltip
        contentClassName={s.tooltip}
        content={
          <div className={s.tooltipContent}>
            <div className={s.row}>
              <Label iconName="ShutdownIcon" color="gray-primary" />
              {t('map.legend.off')} <span className={s.count}>{off}</span>
            </div>
            <div className={s.row}>
              <Label iconName="ShutdownIcon" color="green" />
              {t('map.legend.on')} <span className={s.count}>{on}</span>
            </div>
            <div className={s.row}>
              <Label iconName="SeansIcon" color="blue" />
              {t('map.legend.activeSession')}{' '}
              <span className={s.count}>{session}</span>
            </div>
            <div className={s.row}>
              <Label iconName="CheckedCalendarIcon" color="blue" />
              {t('map.legend.booked')}{' '}
              <span className={s.count}>{booking}</span>
            </div>
            <Separator />
            <span className={s.title}>{t('map.legend.service')}</span>
            <div className={s.row}>
              <Label iconName="ConeIcon" color="orange" />
              {t('host.statuses.service')}
              <span className={s.count}>{service}</span>
            </div>
            <div className={s.row}>
              <CirclesIcon />
              {t('map.legend.configuration')}
              <span className={s.count}>{configuration}</span>
            </div>
            <Separator />
            <span className={s.title}>{t('map.legend.warning')}</span>
            <div className={s.row}>
              <Label iconName="TechModeIcon" color="red" />
              {t('host.statuses.highAccess')}{' '}
              <span className={s.count}>{highAccess}</span>
            </div>
            <div className={s.row}>
              <Label iconName="WifiBugSignalIcon" color="red" />
              {t('host.statuses.noConnection')}{' '}
              <span className={s.count}>{lostConnection}</span>
            </div>
            <div className={s.row}>
              <Label iconName="ShellOffIcon" color="red" />
              {t('host.statuses.shellRemoved')}{' '}
              <span className={s.count}>{shellOff}</span>
            </div>
            <div className={s.row}>
              <Label iconName="LockIcon" color="gray-primary" />
              {t('host.statuses.locked')}
              <span className={s.count}>{locked}</span>
            </div>
          </div>
        }
        position="right-top"
      >
        <div className={s.tooltipIcon}>
          <InfoIcon size={ICON_SIZE} />
        </div>
      </Tooltip>
      <div className={s.block}>
        {!!lostConnection && (
          <div className={cn(s.status, s.error)}>
            <ExclamationIcon color="error" size={ICON_SIZE} />
            {t('host.statuses.noConnection')} | {lostConnection}
          </div>
        )}
        {!!highAccess && (
          <div className={cn(s.status, s.warning)}>
            <TechModeIcon color="warning" size={ICON_SIZE} />
            {t('host.statuses.highAccess')} | {highAccess}
          </div>
        )}
        {!!shellOff && (
          <div className={cn(s.status, s.error)}>
            <CircleCloseIcon color="error" size={ICON_SIZE} />
            {t('host.statuses.shellRemoved')} | {shellOff}
          </div>
        )}

        {!!service && (
          <div className={cn(s.status, s.warning)}>
            <ConeIcon color="warning" size={ICON_SIZE} />
            {t('host.statuses.service')} | {service}
          </div>
        )}
      </div>
    </div>
  );
});
